import { Badge } from "@design";
import { useQuery } from "@tanstack/react-query";
import { Palette } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { listBrandsOptions } from "@/api";
import { EmptyState } from "@/components/build/EmptyState";
import { EntityListPage } from "@/components/build/EntityListPage";
import { EntityRow } from "@/components/build/EntityRow";
import { BuildWithAiButton } from "@/features/build/withAi/BuildWithAiButton";
import { BrandIcon } from "./BrandIcon";
import { useDeleteBrand } from "./useDeleteBrand";

/** Build > Brands list. Default brand sorts first, then alphabetical. */
export function BrandsListPage() {
  const { t } = useTranslation(["build", "brands"]);
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const { onDelete, dialog } = useDeleteBrand();

  const query = useQuery(listBrandsOptions({ query: { page_size: 50 } }));
  const brands = useMemo(() => {
    const all = query.data?.data ?? [];
    const searched = search
      ? all.filter(
          (b) =>
            b.name.toLowerCase().includes(search.toLowerCase()) ||
            (b.audience ?? "").toLowerCase().includes(search.toLowerCase()),
        )
      : all;
    // Default-first, then alphabetical.
    return [...searched].sort(
      (a, b) => Number(b.isDefault ?? false) - Number(a.isDefault ?? false) || a.name.localeCompare(b.name),
    );
  }, [query.data, search]);

  return (
    <EntityListPage
      title={t("brands:title")}
      subtitle={t("brands:subtitle")}
      newLabel={t("brands:new")}
      onNew={() => navigate("/build/brands/new")}
      headerActions={<BuildWithAiButton entity="brand" />}
      search={search}
      onSearch={setSearch}
    >
      {brands.map((brand) => (
        <EntityRow
          key={brand.uuid}
          icon={<BrandIcon />}
          name={brand.name}
          description={brand.description}
          meta={
            <>
              {brand.isDefault && <Badge variant="brand">{t("brands:default")}</Badge>}
              {brand.audience && (
                <Badge variant="neutral" size="compact">
                  {brand.audience === "internal" || brand.audience === "external"
                    ? t(`brands:audience.${brand.audience}`)
                    : brand.audience}
                </Badge>
              )}
            </>
          }
          onOpen={() => navigate(`/build/brands/${brand.uuid}`)}
          menuItems={[
            { label: t("build:menu.edit"), onSelect: () => navigate(`/build/brands/${brand.uuid}`) },
            ...(brand.isDefault
              ? []
              : [{ label: t("build:menu.delete"), danger: true, onSelect: () => onDelete(brand) }]),
          ]}
        />
      ))}
      {brands.length === 0 &&
        (search ? (
          <EmptyState title={t("build:list.noResultsTitle")} body={t("build:list.noResultsBody", { query: search })} />
        ) : (
          <EmptyState icon={<Palette size={32} />} title={t("brands:empty.title")} body={t("brands:empty.body")} />
        ))}
      {dialog}
    </EntityListPage>
  );
}
